import { useEffect, useMemo, useRef, useState } from 'react'
import { DynamicDataSheetGrid, createContextMenuComponent, createTextColumn, keyColumn } from 'react-datasheet-grid'
import type { CellProps, Column, ContextMenuComponentProps, ContextMenuItem, DataSheetGridRef } from 'react-datasheet-grid'
import 'react-datasheet-grid/dist/style.css'
// 그리드는 DynamicDataSheetGrid — 기본 DataSheetGrid(Static)는 처음 받은 columns·콜백을 계속 써서 행 끝 칸·점검 표시·공동 편집 표시가 안 바뀐다
import type { DeviceSpecDevice, SpecMachine, SpecMachineItem } from '../../types'
import { PAD, dropBlankRows, useLeaveBlankRow } from './blankRows'
import ChoiceCell from './ChoiceCell'
import type { ChoiceData } from './ChoiceCell'
import { seriesValue } from './fillSeries'
import { deviceLabel, mesCode } from './masterView'
import type { TagLookup } from './masterView'
import { newKey } from './model'
import { markClass } from './presenceMarks'
import type { CellMarks } from './presenceMarks'
import { releaseGrid, useF2Edit } from './useF2Edit'
import TagPickCell from './TagPickCell'
import type { TagPickData } from './TagPickCell'
import { useFillHandle } from './useFillHandle'
import type { SheetCell } from './useSheetTabs'
import { useStartCell } from './useStartCell'
import type { Sel } from './useFillHandle'
import { useLocate } from './useLocate'
import type { CellNote } from './SheetStatus'

// 인터페이스 항목 시트 — 인터페이스 하나의 전송코드(트랜잭션 코드)·설명과 그 값을 읽을 태그(디바이스·주소).
// 태그를 고르면 전송코드는 {인터페이스 ID}_{태그 Alias}, 설명은 태그 설명으로 채워진다 (비었거나 이전 태그 것 그대로일 때 — 직접 고칠 수 있다).
// 엑셀처럼 붙여넣기·채우기 핸들(Ctrl + 끌기 = 자동 증가: 110_CNT1 → 110_CNT2, D100 → D101).
// 태그 주소는 고른 디바이스의 태그 목록에서 고른다 (TagPickCell). [명세 태그] 칸은 연결한 태그의 타입·Alias·설명 (명세에 없으면 주소 칸이 붉게).

const ROW_HEIGHT = 30
const PAD_ROWS = 5

type Row = SpecMachineItem
// 열 순서 = 그리드 선택 영역의 col 번호
const COLUMN_FIELDS = ['code', 'description', 'device', 'address', 'tag'] as const
const TEXT_FIELDS = new Set<string>(['code', 'description', 'address'])

const blankRow = (key: string): Row => ({ key, code: '', description: '', device: '', address: '' })
const isBlank = (r: Row) => !r.device && !r.code.trim() && !r.description.trim() && !r.address.trim()

const MENU_LABELS: Record<ContextMenuItem['type'], string> = {
  INSERT_ROW_BELLOW: '아래에 항목 삽입',
  DUPLICATE_ROW: '항목 복제',
  DELETE_ROW: '항목 삭제',
  DUPLICATE_ROWS: '선택한 항목 복제',
  DELETE_ROWS: '선택한 항목 삭제',
  COPY: '복사',
  CUT: '잘라내기',
  PASTE: '붙여넣기',
}
const ContextMenuBase = createContextMenuComponent(item => (
  <>{MENU_LABELS[item.type]}{'fromRow' in item && <span className="text-gray-400 ml-1">({item.fromRow + 1}~{item.toRow + 1})</span>}</>
))
const ContextMenu = (props: ContextMenuComponentProps) => <ContextMenuBase {...props} />

const text = () => createTextColumn<string>({
  deletedValue: '', continuousUpdates: false,
  parseUserInput: v => v, parsePastedValue: v => v.trim(), formatBlurredInput: v => v ?? '',
})

// 명세 태그 — 연결한 디바이스·주소의 태그 (읽기 전용)
function TagCell({ rowData, columnData }: CellProps<Row, { lookup: TagLookup }>) {
  if (rowData.key.startsWith(PAD)) return null
  if (!rowData.device || !rowData.address.trim()) return <span className="px-2 text-[11px] text-gray-400">연결 안 됨</span>
  const hit = columnData.lookup(rowData.device, rowData.address)
  if (!hit) return <span className="px-2 text-[11px] font-medium text-red-600" title="디바이스 명세에 이 주소의 태그가 없습니다">명세에 없음</span>
  const { tag } = hit
  return (
    <span className="px-2 text-[11px] text-gray-500 truncate" title={[tag.alias && `Alias ${tag.alias}`, tag.description, tag.dataType].filter(Boolean).join(' · ')}>
      {tag.dataType}{tag.alias && <span className="ml-1 font-mono text-gray-700">{tag.alias}</span>}{tag.description && <span className="ml-1">{tag.description}</span>}
    </span>
  )
}

/** 바깥(항목 편집 툴바)에서 표를 다루는 손잡이 — 찾기/바꾸기의 선택 영역 */
export interface ItemGridHandle {
  getSelection: () => Sel | null
  fieldAt: (col: number) => string | undefined
  /** 그 행·칸으로 커서를 옮기고 화면에 보이게 스크롤 (찾기) */
  focusField: (row: number, field: string) => void
  /** 표에서 커서를 뺀다 (팝업 입력칸에 글자가 가게) — 돌려놓는 함수를 준다 */
  release: () => () => void
}

export default function ItemGrid({ machine, devices, lookup, dupCodes, remote, height, startCell, onCell, onChange, onLocate, onNote, handleRef }: {
  machine: SpecMachine
  devices: DeviceSpecDevice[]
  lookup: TagLookup
  dupCodes: Set<string>
  remote?: CellMarks // 다른 사람이 있는 칸 (항목 키 → 칸 → 색)
  height: number
  startCell?: SheetCell | null // 처음 놓을 커서 자리 (단축키로 탭을 옮겼을 때)
  onCell?: (cell: SheetCell | null) => void // 지금 커서 자리
  onChange: (items: SpecMachineItem[]) => void
  onLocate?: (itemKey: string | null, field: string | null, editing: boolean) => void
  onNote?: (note: CellNote | null) => void // 지금 칸이 붉은·노란 이유 (상태 줄)
  handleRef?: (handle: ItemGridHandle | null) => void
}) {
  const gridRef = useRef<DataSheetGridRef>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  useF2Edit(gridRef)

  // 마운트 동안 한 번 등록 (탭마다 새 표)
  const handleRefRef = useRef(handleRef)
  useEffect(() => { handleRefRef.current = handleRef })
  useEffect(() => {
    const register = handleRefRef.current
    register?.({
      getSelection: () => {
        const grid = gridRef.current
        const sel = grid?.selection ?? (grid?.activeCell ? { min: grid.activeCell, max: grid.activeCell } : null)
        return sel ? { min: { col: sel.min.col, row: sel.min.row }, max: { col: sel.max.col, row: sel.max.row } } : null
      },
      fieldAt: col => COLUMN_FIELDS[col],
      focusField: (row, field) => {
        const grid = gridRef.current
        if (!grid) return
        grid.setActiveCell({ col: Math.max(0, (COLUMN_FIELDS as readonly string[]).indexOf(field)), row })
        setTimeout(() => wrapRef.current?.querySelector('.dsg-active-cell')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60)
      },
      release: () => releaseGrid(gridRef),
    })
    return () => register?.(null)
  }, [])

  const display = useMemo(
    () => [...machine.items, ...Array.from({ length: PAD_ROWS }, (_, j) => blankRow(`${PAD}${machine.items.length + j}`))],
    [machine.items],
  )
  const displayRef = useRef(display)
  useStartCell(gridRef, startCell, COLUMN_FIELDS.length, display.length)
  useEffect(() => { displayRef.current = display })

  // 지금 칸이 붉은·노란 이유 — 명세에 없는 태그(주소·명세 태그 칸), 겹치는 전송코드
  const [activeCell, setActiveCell] = useState<{ col: number; row: number } | null>(null)
  const note = useMemo<CellNote | null>(() => {
    const item = activeCell ? display[activeCell.row] : undefined
    const field = activeCell ? COLUMN_FIELDS[activeCell.col] : undefined
    if (!item || !field || item.key.startsWith(PAD)) return null
    const at = `${activeCell!.row + 1}행`
    if ((field === 'address' || field === 'tag') && item.device && item.address.trim() && !lookup(item.device, item.address))
      return { tone: 'error', text: `${at} 태그 주소 — 고른 디바이스의 명세에 '${item.address.trim()}' 태그가 없습니다` }
    if (field === 'code' && item.code.trim() && dupCodes.has(item.code.trim()))
      return { tone: 'warn', text: `${at} 전송코드 — 다른 항목과 같습니다. MES 는 전송코드로 값을 구분하므로 하나만 남기세요` }
    return null
  }, [activeCell, display, lookup, dupCodes])
  const onNoteRef = useRef(onNote)
  useEffect(() => { onNoteRef.current = onNote })
  useEffect(() => { onNoteRef.current?.(note) }, [note])
  useEffect(() => () => onNoteRef.current?.(null), [])

  // 행 → 항목. 한 칸이라도 적힌 행만 남기고 (칸을 다 지운 행은 없어진다 — blankRows), 빈 행에 적은 것은 새 키로. 안 바뀐 항목은 같은 객체로 (변경으로 잡히지 않게).
  // 태그를 고르면(디바이스·주소가 바뀌면) 전송코드가 비었거나 이전 태그로 만든 코드일 때 {인터페이스 ID}_{Alias} 로 채우고,
  // 설명이 비었으면 태그 설명으로 — 같이 고친 칸(직접 적은 전송코드 등)은 건드리지 않는다
  const handleChange = (rows: Row[]) => {
    const existing = new Map(machine.items.map(i => [i.key, i]))
    onChange(dropBlankRows(rows, displayRef.current, isBlank).map(r => {
      const prev = existing.get(r.key) ?? blankRow(r.key)
      let { code, description } = r
      if (r.device !== prev.device || r.address !== prev.address) {
        const auto = mesCode(machine.code, lookup, r.device, r.address)
        if (auto && code === prev.code && (!code.trim() || code === mesCode(machine.code, lookup, prev.device, prev.address))) code = auto
        // 설명도 전송코드처럼 — 비었거나 이전 태그 설명 그대로면 새 태그 설명으로
        const tagDesc = lookup(r.device, r.address)?.tag.description.trim()
        const prevDesc = lookup(prev.device, prev.address)?.tag.description.trim() ?? ''
        if (tagDesc && description === prev.description && (!description.trim() || description.trim() === prevDesc)) description = tagDesc
      }
      const item = { key: r.key.startsWith(PAD) ? newKey() : r.key, code, description, device: r.device, address: r.address }
      const old = existing.get(item.key)
      return old && JSON.stringify(old) === JSON.stringify(item) ? old : item
    }))
  }

  const locateCell = useLocate(wrapRef, onLocate)
  const leaveRow = useLeaveBlankRow(gridRef, displayRef, isBlank, key => onChange(machine.items.filter(i => i.key !== key)))
  const fill = useFillHandle({
    wrapRef, rowHeight: ROW_HEIGHT,
    activeCell: () => gridRef.current?.activeCell ?? null,
    increments: sel => ctrl => ctrl && COLUMN_FIELDS.slice(sel.min.col, sel.max.col + 1).some(f =>
      TEXT_FIELDS.has(f) && seriesValue(displayRef.current.slice(sel.min.row, sel.max.row + 1).map(r => r[f as 'code']), 1, true) != null),
    onFill: (sel, target, ctrl) => fillDown(sel, target, ctrl),
  })

  const fillDown = (sel: Sel, target: number, ctrl: boolean) => {
    const out = [...displayRef.current]
    while (out.length <= target) out.push(blankRow(`${PAD}${out.length}`))
    for (const field of COLUMN_FIELDS.slice(sel.min.col, sel.max.col + 1)) {
      if (field === 'tag') continue
      const src = out.slice(sel.min.row, sel.max.row + 1).map(r => r[field])
      for (let row = sel.max.row + 1; row <= target; row++) {
        const k = row - sel.max.row
        const series = TEXT_FIELDS.has(field) ? seriesValue(src, k, ctrl) : null
        out[row] = { ...out[row], [field]: series ?? src[(k - 1) % src.length] }
      }
    }
    handleChange(out)
    requestAnimationFrame(() => gridRef.current?.setSelection({ min: { col: sel.min.col, row: sel.min.row }, max: { col: sel.max.col, row: target } }))
  }

  const columns = useMemo<Column<Row>[]>(() => {
    const cls = (field: string, extra?: (r: Row) => string) => ({ rowData }: { rowData: Row }) =>
      [extra?.(rowData) ?? '', markClass(remote, rowData.key, field)].filter(Boolean).join(' ') || undefined
    const deviceOptions = devices.map(d => ({ value: d.key, label: `${deviceLabel(d)}${d.code ? ` (${d.code})` : ''}` }))
    const findDevice = (v: string) => {
      const s = v.trim().toLowerCase()
      return deviceOptions.find(o => o.label.toLowerCase() === s)?.value
        ?? devices.find(d => d.name.toLowerCase() === s || d.code.toLowerCase() === s)?.key ?? ''
    }
    return [
      {
        ...keyColumn<Row, 'code'>('code', text()),
        title: <span title="MES로 보내는 코드 (예: 110_CNT)">전송코드</span>,
        basis: 170, grow: 1, minWidth: 110,
        cellClassName: cls('code', r => `font-mono ${r.code.trim() && dupCodes.has(r.code.trim()) ? 'dsg-cell-dup' : ''}`),
      },
      { ...keyColumn<Row, 'description'>('description', text()), title: '설명', basis: 200, grow: 2, minWidth: 110, cellClassName: cls('description') },
      {
        ...keyColumn<Row, 'device'>('device', {
          component: ChoiceCell, columnData: { options: deviceOptions, placeholder: '디바이스' } as ChoiceData, keepFocus: true, disableKeys: true,
          deleteValue: () => '', copyValue: ({ rowData }) => deviceOptions.find(o => o.value === rowData)?.label ?? rowData, pasteValue: ({ value }) => findDevice(value),
        } as Partial<Column<string, ChoiceData, string>>),
        title: '디바이스', basis: 170, grow: 1, minWidth: 110, cellClassName: cls('device'),
      },
      {
        // 고른 디바이스의 태그 목록에서 (디바이스가 비면 모든 태그에서 골라 디바이스도 채운다)
        id: 'address', title: <span title="선택한 디바이스의 태그 목록에서 고릅니다">태그 주소</span>,
        basis: 120, grow: 0, minWidth: 90, keepFocus: true, disableKeys: true,
        component: TagPickCell as Column<Row, TagPickData>['component'], columnData: { devices },
        copyValue: ({ rowData }) => rowData.address,
        pasteValue: ({ rowData, value }) => ({ ...rowData, address: value.trim() }),
        deleteValue: ({ rowData }) => ({ ...rowData, address: '' }),
        cellClassName: cls('address', r => (r.device && r.address.trim() && !lookup(r.device, r.address) ? 'dsg-cell-invalid' : '')),
      } as Column<Row, TagPickData>,
      {
        id: 'tag', title: <span title="연결한 태그의 타입, Alias, 설명">명세 태그</span>, basis: 220, grow: 1, minWidth: 110, disabled: true,
        component: TagCell as Column<Row, { lookup: TagLookup }>['component'], columnData: { lookup },
        copyValue: () => null, pasteValue: ({ rowData }) => rowData, deleteValue: ({ rowData }) => rowData,
      } as Column<Row, { lookup: TagLookup }>,
    ] as Column<Row>[]
  }, [devices, lookup, dupCodes, remote])

  return (
    <div ref={wrapRef} className="spec-grid relative">
      <DynamicDataSheetGrid<Row>
        ref={gridRef}
        onActiveCellChange={({ cell }) => {
          fill.onActiveCell(cell, !!gridRef.current?.selection)
          onCell?.(cell ? { col: cell.col, row: cell.row } : null)
          setActiveCell(cell ? { col: cell.col, row: cell.row } : null)
          const key = cell ? display[cell.row]?.key : undefined
          leaveRow(cell)
          locateCell(key && !key.startsWith(PAD) ? key : null, cell ? COLUMN_FIELDS[cell.col] ?? null : null)
        }}
        onSelectionChange={({ selection }) => fill.onSelection(selection)}
        onScroll={fill.place}
        value={display}
        onChange={handleChange}
        columns={columns}
        rowKey="key"
        createRow={() => blankRow(newKey())}
        duplicateRow={({ rowData }) => ({ ...rowData, key: newKey() })}
        rowHeight={ROW_HEIGHT}
        headerRowHeight={ROW_HEIGHT}
        height={height}
        disableSmartDelete
        disableExpandSelection
        addRowsComponent={false}
        contextMenuComponent={ContextMenu}
      />
      {fill.element}
    </div>
  )
}
